import React, { useRef, useImperativeHandle, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { Alert, Button, Typography } from "@curves/ui-base";
import { EmployeeSelect } from '@icoach/components/';
import GradientColorHeader from '@icoach/components/dialog/GradientColorHeader';
import { refIsRequiredError } from '@util/utils';
import useOrderCheckoutApi from '@apis/useOrderCheckoutApi';
import useOrderReturnApi from '@apis/useOrderReturnApi';
import OrderAllowanceItemTable from '@icoach/accountBook/tables/OrderAllowanceItemTable';

const AllowanceVoidDialogContent = React.forwardRef((props, ref) => {
	const { className, employeeOptions = [], items } = props;
	const employeeRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (employeeRef.current && employeeRef.current.getResult) {
					result = Object.assign(result, { employeeID: employeeRef.current.getResult() });
				}
				return result;
			},
			isError: () => refIsRequiredError(employeeRef)
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid className={className} spacing={3} container>
				<Grid xs={12} item>
					<Typography className="font-weight-bold">折讓品項</Typography>
					<OrderAllowanceItemTable className="theme-gray" rowsData={items} />
				</Grid>
				<Grid xs={12} item>
					<EmployeeSelect ref={employeeRef} options={employeeOptions} label="擔當" fullWidth required />
				</Grid>
			</Grid>
		</React.Fragment>
	);
});

export const AllowanceVoidDialog = React.forwardRef((props, ref) => {
	const { className, open, sourceData, onClose, onSubmit } = props;
	const { getOrderCheckoutOptionsApi } = useOrderCheckoutApi();
	const [resource, setResource] = useState({});
	const { coachOptions: employeeOptions = [] } = resource;

	const doInitOptionsApi = async () => {
		let res = await getOrderCheckoutOptionsApi();
		if (res) {
			setResource(res);
		}
	};

	useEffect(
		() => {
			if (open) doInitOptionsApi();
		},
		// eslint-disable-next-line
		[open]
	);
	return (
		<Dialog className={clsx('simple-content', 'dialog', className)} PaperProps={{ className: 'wx-65rem' }} open={open} fullWidth>
			<GradientColorHeader onClose={onClose}>確認折讓作廢</GradientColorHeader>
			<DialogContent className="p-4">
				<AllowanceVoidDialogContent ref={ref} className="mb-3" items={sourceData} employeeOptions={employeeOptions} />
				<Alert className="mb-3" severity="error" variant="filled" color="error">
					請注意，一旦送出作廢將無法恢復
				</Alert>
			</DialogContent>
			<DialogActions className="px-4 pt-0 pb-2">
				<Button variant="contained" onClick={onSubmit}>
					確認作廢
				</Button>
			</DialogActions>
		</Dialog>
	);
});

// 訂單折扣作廢(註解:依據發票折讓作廢的概念，實則底層邏輯依舊是發票折讓作為依據)
const OrderAllowanceVoidDialog = props => {
	const { className, open, allowanceID, items, onClose, refresh } = props;
	const { postOrderAbandonAllowanceConfirmApi } = useOrderReturnApi();
	const contentRef = useRef();

	const doOrderAbandonAllowanceConfirmApi = async params => {
		let res = await postOrderAbandonAllowanceConfirmApi(params);
		if (res) {
			refresh && refresh();
			onClose();
		}
	};

	const getParams = () => {
		let result = { allowanceID };
		if (contentRef.current && contentRef.current.getResult) result = Object.assign(result, contentRef.current.getResult());
		return result;
	};

	const handleSubmit = () => {
		const isError = refIsRequiredError(contentRef);
		if (!isError) {
			const params = getParams();
			doOrderAbandonAllowanceConfirmApi(params);
		}
	};

	return <AllowanceVoidDialog ref={contentRef} className={className} open={open} sourceData={items} onClose={onClose} onSubmit={handleSubmit} />;
};

export default OrderAllowanceVoidDialog;
